import { useRouter } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useMemo, useState } from "react"

import { cx } from "../../../lib/cx"
import { viewerTimeZone } from "../../../lib/format"
import { zoneLabel } from "../../../shell/timezone-note"
import { Button, Checkbox } from "../../../ui"
import "../../screen-tokens.css"
import type { CreateSalonDraft, LegalForm } from "./create-salon-model"
import {
  LEGAL_FORMS,
  TEXT_SLUG_FOREVER,
  checkSlugShape,
  slugFromName,
  validateCreateSalon,
} from "./create-salon-model"
import styles from "./onboarding.module.css"

/**
 * 4-А1 «Создать салон».
 *
 * ЧЕГО ЗДЕСЬ НЕТ И ПОЧЕМУ. Маршрута создания тенанта в API не существует вовсе: ни
 * регистрации салона, ни проверки «свободен ли адрес». Поэтому форма собрана целиком
 * и работает целиком — кроме последнего шага, который честно сказано, что не работает.
 * Обратный вариант («кнопка нажимается, а потом 404») хуже: человек успевает поверить,
 * что салон создан, и начинает ждать письма.
 *
 * ДВА СОГЛАСИЯ — ДВЕ ОТМЕТКИ. Оферта и политика обработки данных — разные документы,
 * и одна галочка «принимаю условия» согласием на обработку персональных данных
 * не является: оно обязано быть конкретным и отделимым. Правило живёт
 * в create-salon-model.ts под проверками, чтобы его не «упростили» ради красоты формы.
 */

const CREATE_BLOCKED =
  "Создание салона пока не работает: в API нет маршрута регистрации. Форма готова и включится, как только он появится."

export function CreateSalonScreen(): ReactNode {
  const router = useRouter()

  const [salonName, setSalonName] = useState("")
  const [slugEdited, setSlugEdited] = useState(false)
  const [slug, setSlug] = useState("")
  const [contact, setContact] = useState("")
  const [ownerName, setOwnerName] = useState("")
  const [legalForm, setLegalForm] = useState<LegalForm>("ИП")
  const [offerAccepted, setOfferAccepted] = useState(false)
  const [privacyAccepted, setPrivacyAccepted] = useState(false)

  // Зона определяется по устройству. В черновик уходит МАШИННОЕ имя (Asia/Novosibirsk) —
  // его ждёт сервер, — а человеку показывается город и сдвиг: «Новосибирск (UTC+7)».
  // Молча взятая зона уводит весь день салона на час, а подтвердить строку из кода
  // человек не может: он её не читает.
  const timezone = useMemo(() => viewerTimeZone(), [])
  const timezoneText = useMemo(() => zoneLabel(timezone, new Date()), [timezone])

  const draft: CreateSalonDraft = {
    salonName,
    slug,
    contact,
    ownerName,
    timezone,
    legalForm,
    offerAccepted,
    privacyAccepted,
  }

  const shape = checkSlugShape(slug)
  const validation = validateCreateSalon(draft, shape.kind === "invalid" ? shape : { kind: "idle" })

  return (
    <div className={styles.page}>
      <div className={styles.column}>
        <div>
          <h1 className={styles.heading}>Создать салон</h1>
          <p className={styles.subheading}>
            Минута — и у вас будет кабинет. Без оплаты и без менеджера.
          </p>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Название салона</span>
          <input
            className={styles.control}
            value={salonName}
            placeholder="Барбершоп на Ленина"
            onChange={(event) => {
              const value = event.target.value
              setSalonName(value)
              // Адрес подставляется, пока его не трогали руками: набирать латиницей
              // название, которое уже написано выше, — лишняя работа на телефоне.
              if (!slugEdited) setSlug(slugFromName(value))
            }}
          />
          {validation.salonNameError === null || salonName.length === 0 ? null : (
            <small className={styles.hintError}>{validation.salonNameError}</small>
          )}
        </label>

        <div className={styles.field}>
          <span className={styles.fieldLabel} id="slug-label">
            Адрес страницы записи
          </span>
          <div className={styles.slugRow}>
            <input
              className={styles.slugInput}
              aria-labelledby="slug-label"
              value={slug}
              placeholder="barbershop-lenina"
              onChange={(event) => {
                setSlugEdited(true)
                setSlug(event.target.value.toLowerCase())
              }}
            />
            <span className={styles.slugSuffix}>.planerius.ru</span>
          </div>
          {shape.kind === "invalid" ? (
            <small className={styles.hintError}>{shape.reason}</small>
          ) : slug.length > 0 ? (
            /*
             * «✓ Свободен» из макета здесь не показывается: проверки занятости в API нет,
             * а зелёная галочка означала бы «адрес ваш» — обещание, которого кабинет дать
             * не может. Вместо неё — то, что действительно проверено: вид адреса.
             */
            <small className={styles.hint}>Вид адреса подходит. Занятость проверит сервер.</small>
          ) : null}
          <small className={styles.hint}>{TEXT_SLUG_FOREVER}</small>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Ваш телефон или почта</span>
          <input
            className={cx(styles.control)}
            inputMode="tel"
            value={contact}
            placeholder="+7 916 123-45-67"
            onChange={(event) => {
              setContact(event.target.value)
            }}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Как вас называть</span>
          <input
            className={styles.control}
            value={ownerName}
            placeholder="Алексей"
            onChange={(event) => {
              setOwnerName(event.target.value)
            }}
          />
        </label>

        <div className={styles.row2}>
          <div className={styles.field}>
            <span className={styles.fieldLabel} id="tz-label">
              Часовой пояс
            </span>
            <input
              className={styles.control}
              value={timezoneText}
              readOnly
              aria-labelledby="tz-label"
            />
            <small className={styles.hint}>Определили по устройству</small>
          </div>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Форма</span>
            <select
              className={styles.control}
              value={legalForm}
              onChange={(event) => {
                setLegalForm(event.target.value as LegalForm)
              }}
            >
              {LEGAL_FORMS.map((form) => (
                <option key={form} value={form}>
                  {form}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className={styles.consents}>
          <Checkbox checked={offerAccepted} onChange={setOfferAccepted}>
            Принимаю оферту
          </Checkbox>
          <div className={styles.consentDivider} />
          <Checkbox checked={privacyAccepted} onChange={setPrivacyAccepted}>
            Согласен с политикой обработки данных
          </Checkbox>
          {validation.consentError === null ? null : (
            <small className={styles.hintError}>{validation.consentError}</small>
          )}
        </div>

        <p className={styles.hint}>{CREATE_BLOCKED}</p>
      </div>

      <div className={styles.footer}>
        <Button variant="primary" deniedReason={CREATE_BLOCKED}>
          Создать салон
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            void router.navigate({ to: "/login", search: {} })
          }}
        >
          Вернуться ко входу
        </Button>
      </div>
    </div>
  )
}
